<cs-header>
  <cs-header-title>
    @if (slot) {
      <span class="clickable-title" (click)="showMoreMenu($event)">
        <span>{{ slot.nickname }}</span>
        <cs-icon name="ellipsis-vertical"></cs-icon>
      </span>
    }
  </cs-header-title>
</cs-header>

<cs-content>
  @if (slot) {
    <!-- #4530: this client has been parked. Said at the top of the page, and in
         full words rather than only an icon, because every action below behaves
         differently while it is true. -->
    @if (isAsleep) {
      <div class="asleep-notice">
        <cs-icon name="moon"></cs-icon>
        <span>{{ asleepForText() }}</span>
      </div>
    }
    <!-- #4817: the client accepted the invitation, but their account never
         received what the claim was supposed to put there. Said at the top,
         with what to do about it, because everything else on this page — the
         intake the coach filled, the program they built — describes a handover
         that did not land, and reads as healthy until this says otherwise. -->
    @if (hasBrokenHandover) {
      <div class="handover-notice">
        <cs-icon name="warning"></cs-icon>
        <span>{{ LL.coach.dashboard.handoverIncompleteExplanation() }}</span>
      </div>
    }
    <!-- The invitation link section, written once and rendered from the two places that show it:
         the option-A block (intake filled, waiting for the client to claim) and the option-B block
         (blank intake sent, nobody has claimed yet). It used to be written out twice, so every
         change to it had to be made twice with nothing checking the two copies stayed in step. It
         reads only component fields, never `slot`, so it needs no context passed in. -->
    <ng-template #inviteSection>
      <div class="invite-section">
        <div class="invite-description">{{ LL.coach.invite.description() }}</div>
        @if (loadingLink) {
          <div class="invite-creating">
            <cs-spinner></cs-spinner>
            <div class="invite-creating-text">{{ LL.coach.invite.creatingLink() }}</div>
          </div>
        } @else if (claimUrl) {
          <div class="link-box" (click)="copyLink()">
            <span class="link-text" id="claimLinkText">{{ claimUrl }}</span>
            <cs-icon name="copy"></cs-icon>
          </div>
        } @else if (claimLinkError) {
          <!-- #4447: without this branch the coach saw the description above and then nothing at
               all, which reads as "this client has no link yet" rather than "the request failed".
               No id here on purpose — this section is rendered from two places on the page, and
               one id used twice is a lookup that silently finds only the first. -->
          <div class="invite-error">{{ claimLinkError }}</div>
          <cs-button tertiary small [label]="LL.common.buttons.tryAgain()" (click)="retryClaimLink()"></cs-button>
        }
      </div>
    </ng-template>

    <cs-item-list>

      <!-- pending slot, no intake yet, and no intake mode chosen yet: offer the two options (option A / option B) -->
      @if (!hasIntake && isPending && !isClientFills) {
        <div class="intake-choice">
          <div class="intake-choice-title">{{ LL.coach.intakeChoice.title() }}</div>
          <div class="intake-choice-subtext">{{ LL.coach.intakeChoice.subtext() }}</div>
          <div class="intake-choice-option" (click)="chooseCoachFillsIntake()">
            <div class="intake-choice-option-title">{{ LL.coach.intakeChoice.fillMyself() }}</div>
            <div class="intake-choice-option-subtext">{{ LL.coach.intakeChoice.fillMyselfSubtext() }}</div>
          </div>
          <div class="intake-choice-option" (click)="chooseSendBlankIntake()">
            <div class="intake-choice-option-title">{{ LL.coach.intakeChoice.sendBlank() }}</div>
            <div class="intake-choice-option-subtext">{{ LL.coach.intakeChoice.sendBlankSubtext() }}</div>
          </div>
        </div>
      }

      <!-- option A: intake filled, waiting for client to claim -->
      @if (hasIntake && !isClaimed) {
        <div class="plan-summary-row">
          <div class="plan-summary">
            {{ getPlanSummary() }}
          </div>
          <cs-button tertiary small [label]="LL.coach.editProgram()" (click)="editProgram()"></cs-button>
        </div>
        <ng-container *ngTemplateOutlet="inviteSection"></ng-container>
      }

      <!-- option B (#3389): coach has chosen "send blank intake" but no client has claimed yet -->
      @if (isClientFillsPending) {
        <ng-container *ngTemplateOutlet="inviteSection"></ng-container>
      }

      <!-- option B (#3389): client has claimed but not yet finished the intake -->
      @if (isClientFillsInvited) {
        <div class="client-info">
          <cs-label>{{ slot.nickname }}</cs-label>
          <cs-badge [label]="LL.coach.clientFills.invitedTitle()" color="yellow"></cs-badge>
        </div>
        <div class="invite-description">{{ LL.coach.clientFills.invitedSubtext() }}</div>
      }

      <!-- option B (#3389): client has finished the intake; coach can review -->
      @if (isClientFillsSubmitted) {
        <div class="client-info">
          <cs-label>{{ slot.nickname }}</cs-label>
          <cs-badge [label]="LL.coach.clientFills.submittedTitle()" color="blue"></cs-badge>
        </div>
        <div class="invite-description">{{ LL.coach.clientFills.submittedSubtext() }}</div>
      }

      <!-- option A claimed slot, or option B finalized slot: normal active client view -->
      @if (isProgramReady) {
        <div class="client-info">
          <cs-label>{{ slot.nickname }}</cs-label>
          <!-- #4530: a parked client gets the neutral "Asleep" badge instead of
               the green "Claimed" one, matching the client list. The green badge
               reads as coaching that is currently happening, which is exactly
               what parking suspends. -->
          @if (isAsleep) {
            <cs-badge [label]="LL.coach.clientAsleep.badge()" [color]="asleepBadgeColor"></cs-badge>
          } @else {
            <cs-badge [label]="LL.coach.status.claimed()" color="green"></cs-badge>
          }
        </div>

        <!-- Pending action items: weekly checkups + strength tests awaiting coach review.
             Fetched on enter from the same 30-day window the dashboard uses.
             #4530: not shown for a parked client — the coach is refused entry to
             them, so every row here would be something they cannot act on. The
             fetch is skipped for the same reason (see ionViewWillEnter). -->
        @if (isAsleep) {
          <!-- nothing: parked clients have no actionable items -->
        } @else if (loadingActionItems) {
          <div class="action-items-section">
            <div class="action-items-title">{{ LL.coach.dashboard.actionItemsSectionTitle() }}</div>
            <cs-skeleton width="100%" height="48px"></cs-skeleton>
          </div>
        } @else if (actionItems && actionItems.length > 0) {
          <div class="action-items-section">
            <div class="action-items-title">{{ LL.coach.dashboard.actionItemsSectionTitle() }}</div>
            @for (item of actionItems; track item.logId) {
              @if (areActionItemsClickable) {
                <!-- #5456: with the coaching dashboard on, a row opens the thing to review in the client's account -->
                <div class="action-item-row clickable" role="button" tabindex="0"
                  (click)="onActionItemClick($event, item)" (keydown.enter)="onActionItemClick($event, item)"
                  (keydown.space)="onActionItemClick($event, item)">
                  <div class="action-item-body">
                    <div class="action-item-title">{{ getActionItemTitle(item) }}</div>
                    <div class="action-item-subtext">{{ LL.coach.dashboard.actionItemSubtext() }}</div>
                  </div>
                  <div class="action-item-date">{{ getActionItemTimeAgo(item) }}</div>
                  <cs-icon name="arrow-dropright" [ngClass]="'action-item-chevron'"></cs-icon>
                </div>
              } @else {
                <div class="action-item-row">
                  <div class="action-item-body">
                    <div class="action-item-title">{{ getActionItemTitle(item) }}</div>
                    <div class="action-item-subtext">{{ LL.coach.dashboard.actionItemSubtext() }}</div>
                  </div>
                  <div class="action-item-date">{{ getActionItemTimeAgo(item) }}</div>
                </div>
              }
            }
          </div>
        }
      }
    </cs-item-list>
  }
</cs-content>

<cs-page-action-buttons>
  <!-- option A empty pending slot: the chooser block above has its own click handlers for both options, so no extra action button is rendered here -->
  <!-- option A: intake filled, waiting for client to claim, share the link -->
  @if (hasIntake && isPending && !loadingLink && claimUrl) {
    <cs-button medium primary [label]="LL.coach.invite.share()" icon="send" (click)="shareLink()"></cs-button>
  }
  <!-- option B (#3389): coach has chosen blank invite, link ready to share -->
  @if (isClientFillsPending && !loadingLink && claimUrl) {
    <cs-button medium primary [label]="LL.coach.invite.share()" icon="send" (click)="shareLink()"></cs-button>
  }
  <!-- option B (#3389): client has claimed, has not yet finished — stall fallback -->
  @if (isClientFillsInvited) {
    <cs-button medium secondary [label]="LL.coach.clientFills.fillMyself()" (click)="fillIntakeMyself($event)"></cs-button>
  }
  <!-- option B (#3389): client has finished, coach reviews and finalizes -->
  @if (isClientFillsSubmitted) {
    <cs-button medium primary [label]="LL.coach.clientFills.reviewIntake()" (click)="reviewIntake($event)"></cs-button>
  }
  <!-- option A claimed slot, or option B finalized slot: normal Manage button -->
  @if (isProgramReady) {
    <cs-button medium primary [label]="LL.coach.manageClient()" (click)="manageClient($event)"></cs-button>
  }
</cs-page-action-buttons>
